"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { Sparkles } from "lucide-react";
import { useMemo, useRef, type CSSProperties } from "react";

interface FeatureRow {
  id?: string | number;
  icon?: string;
  alt_icon?: string;
  title?: string;
  description?: string;
  priority?: number;
  status?: boolean;
}

interface BgRow {
  id?: string | number;
  image?: string;
  mobile_image?: string;
  alt_image?: string;
  mobile_alt_image?: string;
  priority?: number;
  status?: boolean;
}

interface BannerFeatureGridData {
  metadata?: FeatureRow[];
  metadata_1?: BgRow[];
  title_1?: string;
  title_1_color?: string;
  subtitle_color?: string;
  gradient_color_1?: string;
  gradient_color_2?: string;
  background_color?: string;
  hero_section_height?: number;
  hero_container_max_width?: number;
  status?: boolean;
}

function pickBgRow(rows: BgRow[] | undefined): BgRow | null {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const active = rows
    .filter((r) => r !== null && r !== undefined && r.status !== false)
    .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0));
  return active[0] ?? null;
}

/**
 * Renders headline text with `{…}`-wrapped segments in the accent colour, the
 * rest in baseColor. Braces are stripped from the output.
 * e.g. "From Full Stack to {AI-Native Systems}" → accent on "AI-Native Systems".
 */
function renderAccentBraces(
  text: string,
  baseColor: string,
  accentColor: string,
) {
  return text.split(/(\{[^}]*\})/g).map((part, i) => {
    if (!part) return null;
    const braced = part.length > 1 && part[0] === "{" && part.endsWith("}");
    return (
      <span key={i} style={{ color: braced ? accentColor : baseColor }}>
        {braced ? part.slice(1, -1) : part}
      </span>
    );
  });
}

const BannerFeatureGrid = (props: { data?: BannerFeatureGridData }) => {
  const { data: sectionData } = props;
  const bannerRef = useRef<HTMLElement | null>(null);

  const {
    metadata = [],
    metadata_1 = [],
    title_1 = "",
    title_1_color = "#FFFFFF",
    subtitle_color = "#F5C451",
    gradient_color_1 = "rgba(0, 0, 0, 0.55)",
    gradient_color_2 = "rgba(0, 0, 0, 0.78)",
    background_color = "#0a1628",
    hero_section_height = 720,
    hero_container_max_width = 1200,
    status = true,
  } = sectionData || {};

  const bgRow = useMemo(() => pickBgRow(metadata_1), [metadata_1]);

  const rows = useMemo(() => {
    if (!Array.isArray(metadata)) return [];
    return [...metadata]
      .filter((r) => r && r.status !== false)
      .sort(
        (a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0),
      );
  }, [metadata]);

  const desktopBg = String(bgRow?.image ?? "").trim();
  const mobileBg = String(bgRow?.mobile_image ?? "").trim();
  const bgDesktopSrc = desktopBg || mobileBg;
  const bgMobileSrc = mobileBg || desktopBg;

  const contentMax = Number(hero_container_max_width) || 1200;
  const rawMinH = Number(hero_section_height);
  const hasMinH = Number.isFinite(rawMinH) && rawMinH > 0;

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   FireWidgetViewedEvent(bannerRef, "banner_4");
  // }, []);

  if (!status) {
    return null;
  }

  const accent = subtitle_color?.trim() || "#F5C451";

  return (
    <section
      ref={bannerRef}
      className={cn(
        "relative isolate flex w-full min-w-0 flex-col overflow-hidden",
        // The CMS height is a desktop design target, not a fixed box. Below 1024px the
        // content decides the height; from 1024px up it acts as a floor that still can
        // never outgrow the viewport, so short/zoomed windows don't get a dead band.
        hasMinH && "min-[1024px]:min-h-[min(var(--hero-min-h),100svh)]",
      )}
      style={{
        ...(hasMinH
          ? ({ "--hero-min-h": `${rawMinH}px` } as CSSProperties)
          : null),
        backgroundColor: bgDesktopSrc ? undefined : background_color,
      }}
    >
      {bgDesktopSrc ? (
        <div className="absolute inset-0 z-0">
          <Image
            alt={String(
              bgRow?.mobile_alt_image?.trim() ||
                bgRow?.alt_image ||
                "banner-bg",
            )}
            src={bgMobileSrc || bgDesktopSrc}
            fill
            priority
            fetchPriority="high"
            className="object-cover object-center min-[1024px]:hidden"
            sizes="100vw"
          />
          <Image
            alt={String(bgRow?.alt_image || "banner-bg")}
            src={bgDesktopSrc || bgMobileSrc}
            fill
            priority
            fetchPriority="high"
            className="hidden object-cover object-center min-[1024px]:block"
            sizes="100vw"
          />
        </div>
      ) : null}

      <div
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          background: `linear-gradient(180deg, ${gradient_color_1}, ${gradient_color_2})`,
        }}
        aria-hidden
      />

      <div
        className="relative z-10 mx-auto flex w-full min-w-0 flex-1 flex-col justify-center px-4 py-12 min-[640px]:px-6 min-[768px]:py-14 min-[1024px]:px-8 min-[1024px]:py-16"
        style={{ maxWidth: contentMax }}
      >
        <header className="mb-10 text-center min-[768px]:mb-12 min-[1024px]:mb-14">
          {title_1 ? (
            <h2
              className="text-[24px] font-semibold leading-tight min-[768px]:text-[32px] [font-family:var(--font-poppins),Poppins,sans-serif]"
            >
              {renderAccentBraces(title_1, title_1_color, accent)}
            </h2>
          ) : null}
        </header>

        {rows.length > 0 ? (
          <div className="mx-auto flex w-full flex-wrap justify-center gap-x-8 gap-y-8 min-[768px]:max-w-[640px] min-[768px]:gap-y-10 min-[1024px]:max-w-[960px]">
            {rows.map((row, i) => {
              const iconSrc = String(row.icon ?? "").trim();
              return (
                <article
                  key={String(row.id ?? `${row.title}-${i}`)}
                  className={cn(
                    "flex min-w-0 basis-full flex-col gap-3 items-center text-center min-[768px]:basis-[calc((100%-2rem)/2)] min-[768px]:items-start min-[768px]:text-left",
                    rows.length >= 3 && "min-[1024px]:basis-[calc((100%-4rem)/3)]",
                  )}
                >
                  <div className="flex shrink-0 items-center justify-center min-[768px]:justify-start">
                    {iconSrc ? (
                      <div className="relative h-10 w-10 shrink-0">
                        <Image
                          src={iconSrc}
                          alt={row.alt_icon || row.title || "Feature"}
                          width={40}
                          height={40}
                          className="h-10 w-10 object-contain"
                        />
                      </div>
                    ) : (
                      <Sparkles
                        className="h-9 w-9 shrink-0"
                        strokeWidth={1.5}
                        style={{ color: accent }}
                        aria-hidden
                      />
                    )}
                  </div>
                  {row.title ? (
                    <h3 className="text-[16px] font-bold leading-snug text-white min-[640px]:text-[17px] [font-family:var(--font-poppins),Poppins,sans-serif]">
                      {row.title}
                    </h3>
                  ) : null}
                  {row.description ? (
                    <p className="text-[14px] font-normal leading-relaxed text-white/90 min-[640px]:text-[15px]">
                      {row.description}
                    </p>
                  ) : null}
                </article>
              );
            })}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default BannerFeatureGrid;
